import { QRCodeSVG } from 'qrcode.react'
import { cn } from '../../lib/cn.js'

export interface QRCodeProps {
  /** The whole string the camera should read — an `otpauth://` URI, a link. */
  value: string
  size?: number
  /** What it is, for somebody who cannot see it and needs the text instead. */
  label: string
  className?: string
}

/**
 * Something a phone camera can read.
 *
 * The second-step enrolment hands back a `uri`, and the contract says what it is
 * for in as many words: "The QR an authenticator app scans." Without this, the
 * first administrator on a new platform types a thirty-two character key into a
 * phone by hand, gets one character wrong, and learns so from a code that will
 * not verify.
 *
 * **Always beside the text, never instead of it.** A QR is nothing to somebody
 * using a screen reader, and some authenticator apps still want the key typed —
 * so the screen shows both, and this carries the label for the one that cannot
 * see it.
 *
 * **Always dark on light, even in the dark theme.** It pins itself to the light
 * palette with `data-theme`, because scanners disagree about inverted codes and
 * the one that disagrees is always somebody's.
 *
 * Drawn as SVG rather than canvas, so it survives being printed — which is what
 * people do with the recovery codes sitting next to it.
 */
export function QRCode({ value, size = 168, label, className }: QRCodeProps) {
  return (
    <div
      data-theme="light"
      className={cn(
        'inline-flex rounded-md border border-hairline bg-surface p-3 text-ink',
        className,
      )}
      role="img"
      aria-label={label}
    >
      <QRCodeSVG
        value={value}
        size={size}
        fgColor="currentColor"
        bgColor="transparent"
        // Medium correction: enough to survive glare on a phone screen without
        // making the pattern finer than a camera resolves at this size.
        level="M"
      />
    </div>
  )
}
